import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

type FormValues = z.infer<typeof formSchema>;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { signIn, user, session, activeRole } = useAuth();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    // Check if user is already logged in as admin
    if (user && session && activeRole === 'admin') {
      navigate('/admin/dashboard');
    }
    // If user is logged in with a different role, don't allow direct admin access
    else if (user && session && activeRole && activeRole !== 'admin') {
      toast({
        title: "Please Log In",
        description: "You need to log in as an administrator to access this area.",
      });
    }
  }, [user, session, activeRole, navigate, toast]);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    
    try {
      // Explicitly specify we're logging in as an admin
      const { error } = await signIn(values.email, values.password, 'admin');
      
      if (error) {
        throw new Error(error.message);
      }
      
      toast({
        title: "Login Successful",
        description: "Welcome to the admin dashboard!",
        duration: 6000,
      });
      
      navigate("/admin/dashboard");
    } catch (error: any) {
      console.error("Admin login error:", error);
      toast({
        variant: "destructive",
        title: "Login Error",
        description: error.message || "An unexpected error occurred. Please try again later.",
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen animate-fade-in-up bg-gradient-to-b from-earth-50 via-white to-earth-100">
      <Navbar />
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-4 py-8 pt-16 md:pt-20">
        <div className="w-full max-w-md animate-scale-fade-in">
          <div className="text-left mb-8 animate-fade-in-up">
            <h1 className="text-2xl font-semibold text-earth-700">
              Admin Portal
            </h1>
            <p className="text-earth-700/80">
              Access the administrative dashboard to manage the platform
            </p>
          </div>

          <div className="rounded-xl border border-earth-200/70 bg-white/95 backdrop-blur p-6 shadow-sm animate-fade-in-up animate-delay-200">
            <h2 className="mb-4 text-xl font-semibold text-earth-700">
              Administrator Login
            </h2>
            <p className="mb-6 text-sm text-earth-700/80">
              Enter your credentials to access the admin dashboard
            </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type="email"
                            placeholder="admin@example.com"
                            className="border-neutral-300 bg-neutral-50 pl-10"
                            disabled={loading}
                          />
                          <span className="absolute left-3 top-2.5 text-neutral-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect width="20" height="16" x="2" y="4" rx="2" />
                              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                            </svg>
                          </span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <PasswordInput
                            {...field}
                            placeholder="••••••••"
                            className="border-neutral-300 bg-neutral-50 pl-10"
                            disabled={loading}
                          />
                          <span className="absolute left-3 top-2.5 text-neutral-500 z-10">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          </span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="mt-2 w-full bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Logging in...
                    </>
                  ) : (
                    "Login to Dashboard"
                  )}
                </Button>
              </form>
            </Form>

            <div className="mt-6 text-center text-sm text-neutral-600">
              For administrator access only. If you're a client or practitioner, please use the appropriate portal.
            </div>
          </div>

          <div className="mt-8 text-center animate-fade-in-up animate-delay-600">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                <p className="text-neutral-600">Client Login</p>
                <Link 
                  to="/client-auth" 
                  className="mt-2 inline-block text-amber-700 hover:text-amber-800 hover:underline"
                >
                  Go to Client Portal
                </Link>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                <p className="text-neutral-600">Practitioner Login</p>
                <Link 
                  to="/practitioner-auth" 
                  className="mt-2 inline-block text-amber-700 hover:text-amber-800 hover:underline"
                >
                  Go to Practitioner Portal
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
// 文件已超过200行，建议后续进一步组件化和拆分！
